/// <amd-module name="VSSUI/Components/VssContextualMenu/VssContextualMenu" />

import * as React from "react";
import * as ReactDOM from "react-dom";
import { ContextualMenu, ContextualMenuItemType, IContextualMenuProps } from "OfficeFabric/ContextualMenu";
import { elementContains } from "VSSUI/Utilities/Dom";
import { EventManagement } from "VSS/Core/EventManagement";
import { css, KeyCode } from "VSSUI/Util";
import {
    IVssContextualMenuGroup,
    IVssContextualMenuItem,
    IVssContextualMenuItemProvider,
    IVssContextualMenuProps
} from "../../Components/VssContextualMenu/VssContextualMenu.Props";

import "VSS/LoaderPlugins/Css!VSSUI/Components/VssContextualMenu/VssContextualMenu";

/**
 * State this component manages
 */
/** @deprecated */
export interface IVssContextualMenuState {
    /** The items we're displaying (sorted and with separators and group headings and the like). */
    displayItems: IVssContextualMenuItem[];
    loadingTimeout?: number;
}

interface IGroupInfo extends IVssContextualMenuGroup {
    rank: number; // make rank required
    items: IVssContextualMenuItem[]; // keep a list of items in the group
}

/**
 * Creates office fabric contextual menu, also takes contribution ids to get menu contributions from extensions
 * P.S: ContextualMenu generates some warnings look: https://github.com/OfficeDev/office-ui-fabric-react/issues/357
 */
/** @deprecated Please use import { Menu } from "VSSUI/Menu" */
export class VssContextualMenu extends React.Component<IVssContextualMenuProps, IVssContextualMenuState> {
    private static _activeMenu?: VssContextualMenu;

    private _submenuComponent?: VssContextualMenu;
    private _activeElement?: HTMLElement;
    private _targetElement?: HTMLElement;
    private _isMounted: boolean;

    private timers?: number[];

    // Timeout used to track the closeTimeout on mouseLeave.
    private closeTimeout?: number;

    /** The items we have */
    private _items: IVssContextualMenuItem[];

    /** The groups to display */
    private _groups: IVssContextualMenuGroup[];

    /** Forces the display of group names for development purposes. */
    private _forceGroupNames: boolean;

    protected subMenuId: string;

    private events?: EventManagement;

    public constructor(props: IVssContextualMenuProps) {
        super(props);
        this.timers = [];
        this._items = [];
        this._groups = [];
        this.events = new EventManagement();
        this.state = {
            displayItems: []
        };
    }

    public render(): JSX.Element {
        if (this.state.loadingTimeout) {
            return null;
        }

        const { displayItems } = this.state;
        const { className, directionalHint, gapSpace = 5 } = this.props;

        let hasIcons = !!(this._items && this._items.some(item => !!item.icon || !!item.iconProps));

        this._items.forEach((menuItem: any) => {
            menuItem.menuHasIcons = hasIcons;
            if (hasIcons && !menuItem.iconProps && !menuItem.icon) {
                menuItem.iconProps = { iconName: "" };
            }
        });

        return (
            <ContextualMenu
                gapSpace={gapSpace}
                directionalHint={directionalHint}
                {...this.props}
                items={displayItems}
                className={css("vss-contextualMenu-list", className)}
                onDismiss={this._onDismiss}
                onRenderSubMenu={this._onRenderSubMenu}
            />
        );
    }

    public componentWillMount(): void {
        const loadingTimeout = this._setTimeout(() => {
            this.setState({ loadingTimeout: undefined });
        }, 100);

        this._items = this.props.items || [];
        this._groups = this.props.groups || [];

        this.setState({
            displayItems: this.props.items ? this._arrangeItems(this.props.items) : [],
            loadingTimeout: loadingTimeout
        });

        // Ensure we only have a single root menu open, ignore global processing for submenus.
        if (!this.props.isSubMenu) {
            if (VssContextualMenu._activeMenu) {
                VssContextualMenu._activeMenu.dismiss(null, true);
            }

            VssContextualMenu._activeMenu = this;
        }

        // Save the active element as we are bringing up the menu.
        this._activeElement = document.activeElement as HTMLElement;
    }

    public componentWillUnmount(): void {
        this._isMounted = false;

        if (this.state.loadingTimeout) {
            this._clearTimeout(this.state.loadingTimeout);
        }

        if (this.timers) {
            for (const timer of this.timers) {
                window.clearTimeout(timer);
            }
            this.timers = [];
        }

        if (this.events) {
            this.events.removeAllListeners();
        }

        // If we are the active root menu we can clear our state on closure.
        if (VssContextualMenu._activeMenu === this) {
            delete VssContextualMenu._activeMenu;
        }
    }

    public componentDidMount() {
        this._isMounted = true;

        // Ensure we have resolved the target to an HTMLElement
        if (typeof this.props.target === "string") {
            this._targetElement = document.querySelector(this.props.target) as HTMLElement;
        } else {
            this._targetElement = this.props.target as HTMLElement;
        }

        // If the menu items are being supplied through a callback we will ensure we have
        // start resolving them and setup the loading menu items.
        if (this.props.providers && this.props.providers.length > 0) {
            // create array that will track when providers have indicated they have loaded their intial items
            const uninitializedProviders = this.props.providers.map((x, i) => {
                return i;
            });

            // Invoke each provider
            for (let i = 0; i < this.props.providers.length; i++) {
                const provider: IVssContextualMenuItemProvider = this.props.providers[i];

                // Load the items from the provider
                provider.loadItems(
                    (items: IVssContextualMenuItem[], removedItems?: string[], groupInfo?: IVssContextualMenuGroup[]) => {
                        this._itemsChanged(items, removedItems, groupInfo);
                    },
                    () => {
                        // If loading timeout is still set, see if all providers now initialized.
                        if (this.state.loadingTimeout) {
                            // Look for the provider in the array of providers still loading
                            const indexOfProvider = uninitializedProviders.indexOf(i);
                            if (indexOfProvider > -1) {
                                // If we found it, remove it from the array
                                uninitializedProviders.splice(indexOfProvider, 1);

                                // If no providers still loading, then remove the loading timeout
                                if (this._isMounted && uninitializedProviders.length === 0) {
                                    this.setState({ loadingTimeout: undefined });
                                }
                            }
                        }
                    }
                );
            }
        } else {
            // No providers so load immediately
            this.setState({ loadingTimeout: undefined });
        }

        if (this.props.mouseLeaveDelayMs) {
            // Track the mousemove on the document to close if the mouse is outside the menu for a period of time.
            this.events.addEventListener(document.body, "mousemove", (event: MouseEvent) => {
                if (this._targetElement.contains(event.target as HTMLElement) || this.contains(event.target as HTMLElement)) {
                    if (this.closeTimeout) {
                        this._clearTimeout(this.closeTimeout);
                        delete this.closeTimeout;
                    }
                } else {
                    if (!this.closeTimeout) {
                        this.closeTimeout = this._setTimeout(() => {
                            delete this.closeTimeout;
                            this.dismiss(event, true);
                        }, this.props.mouseLeaveDelayMs || 0);
                    }
                }
            });
        }

        this.events.addEventListener(document, "keydown", (event: KeyboardEvent) => {
            // Invert the display of group names for the opened menus when the ` key pressed.
            if (event.key === "`" && !event.defaultPrevented) {
                this._forceGroupNames = !this._forceGroupNames;
                this.setState({ displayItems: this._arrangeItems(this._items) });
            }

            // If a keystroke was made and we are not focused we need to close the menu.
            // We need to look at shouldFocusOnMount, since it may be an empty menu and we dont want it closed.
            // Don't close the menu for control keys (shift, alt, ctrl)
            else if (
                event.which !== KeyCode.shift &&
                event.which !== KeyCode.ctrl &&
                event.which !== KeyCode.alt &&
                !this.props.isSubMenu &&
                !this.props.shouldFocusOnMount &&
                !this.contains(document.activeElement as HTMLElement)
            ) {
                this.dismiss(event, true);
            }
        });
    }

    public componentWillReceiveProps(nextProps: IVssContextualMenuProps): void {
        if (nextProps.isSubMenu) {
            const oldItems = this.props.items || [];
            const newItems = nextProps.items || [];
            let itemsChanged = oldItems.length !== newItems.length;
            if (!itemsChanged) {
                // Check if same items exist
                itemsChanged = oldItems.some((value: IVssContextualMenuItem) => {
                    return !newItems.some(nextItem => nextItem.key === value.key);
                });
            }

            if (itemsChanged) {
                this.setState({
                    displayItems: newItems ? this._arrangeItems(newItems) : []
                });
            }
        }
    }

    public contains(element: HTMLElement | null): boolean {
        return (
            (this._isMounted && elementContains(ReactDOM.findDOMNode(this) as HTMLElement, element)) ||
            (this._submenuComponent && this._submenuComponent.contains(element))
        );
    }

    public dismiss(reason?: Event, dismissAll?: boolean): void {
        if (this.props.onDismiss) {
            this.props.onDismiss(reason, true);
        }
    }

    /**
     * Sort and group our menu items
     * @param items
     */
    private _arrangeItems(items: IVssContextualMenuItem[], groupInfo?: IVssContextualMenuGroup[]): IVssContextualMenuItem[] {
        const shouldSortMenuItems = items.some(x => x.rank! >= 0);
        const shouldGroupMenuItems = items.some(x => !!x.groupKey);

        items = [...items];
        if (shouldSortMenuItems) {
            this._sortItems(items);
        }

        if (shouldGroupMenuItems) {
            items = this._groupItems(items, groupInfo);
        }

        this._removeConsecutiveSeparators(items);

        return items;
    }

    private _clearTimeout(timeoutId: number): void {
        if (this.timers) {
            const timerIndex = this.timers.indexOf(timeoutId);
            this.timers.splice(timerIndex, 1);
        }
        window.clearTimeout(timeoutId);
    }

    private _invalidateItem = (key: string) => {
        let foundMatch = false;

        // If there is a sub menu, first look for match in the sub menu
        if (this.subMenuId) {
            const matches = this._findMenuItems(this.subMenuId);
            const itemIndex = this._findItemIndex(matches, key);
            foundMatch = this._focusItem(matches, itemIndex - 1);
        }

        // If we did not focus anything in sub menu, try this menu
        if (!foundMatch) {
            const matches = this._findMenuItems(this.props.id);
            const itemIndex = this._findItemIndex(matches, key);
            foundMatch = this._focusItem(matches, itemIndex - 1);

            // check to see if the first match is not the item being removed.  If it is not then focus it.
            if (!foundMatch) {
                if (itemIndex > 0) {
                    foundMatch = this._focusItem(matches, 0);
                } else {
                    this._setTimeout(() => {
                        const matches = this._findMenuItems(this.props.id);
                        this._focusItem(matches, 0);
                    }, 0);
                }
            }
        }

        // If still no matches, then try and focus the previously active element
        if (!foundMatch && this._activeElement && this.contains(document.activeElement as HTMLElement)) {
            this._activeElement.focus();
        }
    };

    private _findMenuItems(menuId: string): NodeListOf<HTMLElement> | undefined {
        const menuElement = document.querySelector("#" + menuId);
        if (menuElement) {
            return menuElement.querySelectorAll("[role='menuitem']") as NodeListOf<HTMLElement>;
        }
    }

    private _findItemIndex(matches: NodeListOf<HTMLElement>, key: string): number {
        let matchedIndex = -1;
        if (matches && matches.length > 1) {
            // Loop through the tabbable items and try and find item which has same key that was just clicked
            for (let i = 0; i < matches.length; i++) {
                const item = matches.item(i);
                if (item.parentElement && item.parentElement.id === key) {
                    matchedIndex = i;
                    break;
                }
            }
        }

        return matchedIndex;
    }

    private _focusItem(matches: NodeListOf<HTMLElement>, itemIndex: number): boolean {
        let itemFocused = false;
        if (matches && matches.length > itemIndex && itemIndex > -1) {
            const item = matches.item(itemIndex);
            item.focus();
            itemFocused = true;
        }

        return itemFocused;
    }

    private _itemsChanged = (items: IVssContextualMenuItem[], removedItems?: string[], groupInfo?: IVssContextualMenuGroup[]) => {
        if (this._isMounted) {
            // Build map of what is currently in the list of menu items
            const existingItemsMap: { [id: string]: IVssContextualMenuItem } = {};
            this._items.forEach(item => {
                // if an item is being removed, then do not add to the existing map.
                if (!removedItems || removedItems.indexOf(item.key) === -1) {
                    existingItemsMap[item.key] = item;
                }
            });

            // Merge in updated items.  This may include updates to existing items or new items
            items.forEach(item => {
                item.invalidateItem = () => {
                    this._invalidateItem(item.key);
                };
                existingItemsMap[item.key] = item;
            });

            const newItems: IVssContextualMenuItem[] = [];
            Object.keys(existingItemsMap).forEach(key => {
                newItems.push(existingItemsMap[key]);
            });

            this._items = newItems;

            this.setState({ displayItems: this._arrangeItems(newItems, groupInfo) });
        }
    };

    /**
     * Arrange the items into groups and put separators between them and headings above them as needed.
     * @param items
     */
    private _groupItems(items: IVssContextualMenuItem[], groupInfo?: IVssContextualMenuGroup[]): IVssContextualMenuItem[] {
        const groupMap: { [id: string]: IGroupInfo } = {};
        let maxGroupRank = 0;
        const ungroupedItems: IVssContextualMenuItem[] = [];
        const forceGroupNames = this._forceGroupNames;

        // gather known groups
        if (this._groups) {
            maxGroupRank = this._groups.reduce((max, g) => (g.rank > max ? g.rank : max), 0);
            for (const g of this._groups) {
                groupMap[g.key] = {
                    key: g.key,
                    name: g.name,
                    rank: g.rank === undefined ? ++maxGroupRank : g.rank,
                    showGroupName: g.showGroupName,
                    items: []
                };
            }
        } else {
            this._groups = [];
        }

        if (groupInfo) {
            for (const g of groupInfo) {
                const existingGroup = groupMap[g.key];
                if (!existingGroup) {
                    groupMap[g.key] = {
                        key: g.key,
                        name: g.name,
                        rank: g.rank === undefined ? ++maxGroupRank : g.rank,
                        showGroupName: g.showGroupName,
                        items: []
                    };

                    this._groups.push(g);
                }
            }
        }

        // put all the items in groups
        for (const i of items) {
            if (i.groupKey) {
                if (groupMap[i.groupKey]) {
                    groupMap[i.groupKey].items.push(i);
                } else {
                    groupMap[i.groupKey] = {
                        key: i.groupKey,
                        name: i.groupKey,
                        rank: ++maxGroupRank,
                        items: [i]
                    };
                }
            } else {
                ungroupedItems.push(i);
            }
        }

        // sort the groups
        const groupList = Object.keys(groupMap).map(n => groupMap[n]);
        groupList.sort((a, b) => (a.rank || Number.MAX_VALUE) - (b.rank || Number.MAX_VALUE));
        groupList.push({
            key: "ungrouped",
            name: "Ungrouped",
            rank: ++maxGroupRank,
            items: ungroupedItems
        });

        // remove dividers from the beginning and end of each group
        groupList.forEach(g => {
            const array = g.items;
            while (array.length > 0 && array[0].itemType === ContextualMenuItemType.Divider) {
                array.shift();
            }
            while (array.length > 0 && array[array.length - 1].itemType === ContextualMenuItemType.Divider) {
                array.pop();
            }
        });

        // merge the groups into the final array
        items = [];
        let first = true;
        for (const g of groupList) {
            if (g.items.length === 0 && !forceGroupNames) {
                continue;
            }

            // add the separator or header for the top of the group
            if ((g.showGroupName && g.name) || forceGroupNames) {
                items.push({
                    key: `group_${g.name}`,
                    itemType: ContextualMenuItemType.Header,
                    name: forceGroupNames ? `${g.name} (key: ${g.key})` : g.name
                });
            } else if (!first) {
                // don't add a separator when there's a header, because header menu items take care
                // of the separator for us
                items.push({
                    key: `divider_${g.name}`,
                    itemType: ContextualMenuItemType.Divider
                });
            }

            if (first) {
                first = false;
            }

            items = items.concat(g.items);
        }

        return items;
    }

    private _onDismiss = (ev?: Event, dismissAll?: boolean): void => {
        // If focus remains in the menu when it is dismissed we will set focus back to the
        // previously focused element.
        if (this._activeElement && this.contains(document.activeElement as HTMLElement)) {
            this._activeElement.focus();
        }

        this.props.onDismiss(ev, dismissAll);
    };

    private _onRenderSubMenu = (props: IContextualMenuProps): JSX.Element => {
        this.subMenuId = props.id;

        if (props.items) {
            props.items.forEach(item => {
                item.invalidateItem = () => {
                    this._invalidateItem(item.key);
                };
            });
        }

        return (
            <VssContextualMenu
                ref={(submenuComponent: VssContextualMenu) => {
                    this._submenuComponent = submenuComponent;
                }}
                {...props as any}
            />
        );
    };

    private _removeConsecutiveSeparators(items: IVssContextualMenuItem[]): void {
        let lastWasSeparator = true;
        for (let i = items.length - 1; i >= 0; i--) {
            const currentIsSeparator = items[i].itemType === ContextualMenuItemType.Divider || items[i].name === "-";
            if (currentIsSeparator && lastWasSeparator) {
                items.splice(i, 1);
            }
            lastWasSeparator = currentIsSeparator;
        }
    }

    private _setTimeout(callback: (...args: any[]) => void, milliseconds?: number, ...args: any[]): number {
        const timerId = window.setTimeout(callback, milliseconds, ...args);
        if (!this.timers) {
            this.timers = [];
        }
        this.timers.push(timerId);
        return timerId;
    }

    /**
     * Sort the items according to their rank property.
     * @param items
     */
    private _sortItems(items: IVssContextualMenuItem[]): void {
        // This is not a stable sort. If we pass items with the same rank, we may not always get them back in the
        // same order. We have a stable sort in vssf webplatform, but we're not taking a dependency there.
        items.sort((a, b) => {
            const aRank = a.rank || Number.MAX_VALUE;
            const bRank = b.rank || Number.MAX_VALUE;
            return aRank - bRank;
        });
    }
}
